import { type KeycloakTokenResponse, type KeycloakTokens } from "~/features/auth/types/keycloak";

const STORAGE_KEYS = {
  TOKENS: "keycloak_tokens",
  STATE: "oauth_state",
  CODE_VERIFIER: "pkce_code_verifier",
  PRE_AUTH_URL: "pre_auth_url",
  FORCE_LOGIN_PROMPT: "force_login_prompt",
} as const;

// Tokens use sessionStorage — cleared when the tab closes, reducing the
// window of exposure if an XSS vulnerability is ever introduced.

export const storeTokens = (tokens: KeycloakTokenResponse): KeycloakTokens => {
  const enriched: KeycloakTokens = { ...tokens, expires_at: Date.now() + tokens.expires_in * 1000 };

  sessionStorage.setItem(STORAGE_KEYS.TOKENS, JSON.stringify(enriched));

  return enriched;
};

export const getStoredTokens = (): KeycloakTokens | null => {
  const stored = sessionStorage.getItem(STORAGE_KEYS.TOKENS);

  if (!stored) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(stored);

    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof (parsed as Record<string, unknown>).access_token !== "string" ||
      typeof (parsed as Record<string, unknown>).refresh_token !== "string" ||
      typeof (parsed as Record<string, unknown>).expires_at !== "number"
    ) {
      sessionStorage.removeItem(STORAGE_KEYS.TOKENS);
      return null;
    }

    return parsed as KeycloakTokens;
  } catch {
    sessionStorage.removeItem(STORAGE_KEYS.TOKENS);

    return null;
  }
};

export const clearTokens = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.TOKENS);
  // Also clear OAuth flow state (lives in localStorage for cross-redirect)
  localStorage.removeItem(STORAGE_KEYS.STATE);
  localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
};

// ---------------------------------------------------------------------------
// OAuth flow state uses localStorage — it must survive the Keycloak redirect
// (which opens in the same tab, so sessionStorage would work, but localStorage
// is the safer choice for edge-cases like browser restores after a crash).
// These values are short-lived and cleared immediately after the callback.
// ---------------------------------------------------------------------------

export const storeOAuthState = (state: string, codeVerifier: string): void => {
  localStorage.setItem(STORAGE_KEYS.STATE, state);
  localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
};

export const getOAuthState = (): { state: string | null; codeVerifier: string | null } => ({
  state: localStorage.getItem(STORAGE_KEYS.STATE),
  codeVerifier: localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER),
});

export const clearOAuthState = (): void => {
  localStorage.removeItem(STORAGE_KEYS.STATE);
  localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
};

// ---------------------------------------------------------------------------
// Pre-auth URL uses localStorage — must survive the Keycloak redirect.
// ---------------------------------------------------------------------------

export const storePreAuthUrl = (url: string): void => {
  localStorage.setItem(STORAGE_KEYS.PRE_AUTH_URL, url);
};

export const getAndClearPreAuthUrl = (): string | null => {
  const url = localStorage.getItem(STORAGE_KEYS.PRE_AUTH_URL);

  localStorage.removeItem(STORAGE_KEYS.PRE_AUTH_URL);

  return url;
};

// ---------------------------------------------------------------------------
// "Force credential prompt on next login" flag — set on explicit logout so the
// following login shows the Keycloak form (account switch) instead of silently
// reusing a surviving SSO session. localStorage so it survives the logout
// redirect; one-shot, consumed by the next initiateLogin.
// ---------------------------------------------------------------------------

export const setForceLoginPrompt = (): void => {
  localStorage.setItem(STORAGE_KEYS.FORCE_LOGIN_PROMPT, "1");
};

export const consumeForceLoginPrompt = (): boolean => {
  const flag = localStorage.getItem(STORAGE_KEYS.FORCE_LOGIN_PROMPT);

  localStorage.removeItem(STORAGE_KEYS.FORCE_LOGIN_PROMPT);

  return flag === "1";
};
